import {
  CandidateSummaryNiceToHaveAssessment,
  CandidateSummaryNiceToHaveStatus,
  CandidateSummaryPayload,
  CandidateSummaryRecommendation,
  CandidateSummaryRequiredSkillAssessment,
  CandidateSummarySkillSource,
  CandidateSummarySkillStatus,
} from '../models/candidate-summary.model';
import { gapsForClientExport } from './candidate-summary-gaps';

export const CANDIDATE_ASSESSMENT_DISCLAIMER =
  'Prepared for recruiter review. Does not replace recruiter judgment or guarantee client acceptance.';

export const ASSESSMENT_SECTIONS = {
  documentTitle: 'Candidate Presentation Brief',
  documentSubtitle:
    'Client-ready brief for sourcers and recruiters on featured Easy Apply roles.',
  overallRecommendation: 'Overall recommendation',
  recruiterSummary: 'Recruiter summary',
  mustHaveSkills: 'Must-have skills',
  mustHaveSkillsIntro:
    'Required for the role — how the resume supports each skill.',
  preferredSkills: 'Preferred skills (nice-to-have)',
  preferredSkillsIntro:
    'Helpful but not required — evidence found on the resume.',
  keyStrengths: 'Key strengths',
  gapsAndRisks: 'Gaps and risks to discuss',
  gapsReviewTitle: 'Gaps requiring recruiter review',
  gapsReviewIntro:
    'These items were flagged by the AI assessment. Review each gap, update the wording if needed, and resolve them before exporting a client-ready PDF.',
  gapsOpenAlert:
    'Open gap(s) must be reviewed and resolved before you can export a client-ready PDF.',
  gapResolutionNote: 'How this was addressed for the client',
  gapStatusOpen: 'Open — needs review',
  gapStatusResolved: 'Resolved for client sharing',
  gapHistoryTitle: 'Gap review history',
  gapHistoryIntro:
    'Internal audit trail of how gaps were reviewed, edited, and closed before client export. This is not included in the PDF.',
  gapHistoryInternalOnly: 'Recruiter only',
  gapHistoryPreviousWording: 'Previous wording',
  gapHistoryPreviousNote: 'Previous note',
  gapHistoryEmptyFilter: 'No closure activity matches this filter.',
  interviewRecommendation: 'Interview recommendation',
  interviewTalkingPoints: 'Interview talking points',
} as const;

/** Recruiter workflow — shown as steps in the presentation brief panel. */
export const PRESENTATION_BRIEF_WORKFLOW = {
  emptyTitle: 'No Presentation Brief Yet',
  emptyIntro:
    'Turn this application into a client-ready brief with must-have skill checks, resume evidence, and interview talking points.',
  steps: [
    {
      title: 'Generate Assessment',
      description: 'AI drafts a brief from the job post and candidate resume.',
    },
    {
      title: 'Review Gaps',
      description:
        'Resolve flagged gaps and edit wording before client sharing.',
    },
    {
      title: 'Export PDF',
      description: 'Download a polished PDF once all gaps are resolved.',
    },
  ] as const,
  status: {
    notStarted: 'Not started',
    generating: 'Generating…',
    needsReview: 'Needs gap review',
    readyToExport: 'Ready to export',
    editing: 'Editing',
  },
  nextAction: {
    generate:
      'Start by generating an assessment from the resume and job description.',
    reviewGaps: 'Resolve open gaps below, then export a client-ready PDF.',
    export: 'All gaps are resolved — export the PDF or edit the brief first.',
  },
  exportBlockedHint:
    'Resolve all open gaps before exporting a client-ready PDF.',
} as const;

export const gapStatusLabel: Record<'OPEN' | 'RESOLVED', string> = {
  OPEN: 'Open',
  RESOLVED: 'Resolved',
};

export const gapStatusColor: Record<'OPEN' | 'RESOLVED', string> = {
  OPEN: 'error',
  RESOLVED: 'success',
};

export const recommendationLabel: Record<
  CandidateSummaryRecommendation,
  string
> = {
  STRONG_MATCH: 'Strong match',
  PARTIAL_MATCH: 'Partial match',
  WEAK_MATCH: 'Weak match',
};

export const recommendationClientGuidance: Record<
  CandidateSummaryRecommendation,
  string
> = {
  STRONG_MATCH: 'Recommend presenting to the client',
  PARTIAL_MATCH: 'Review with the hiring team before presenting',
  WEAK_MATCH: 'Not recommended for client presentation at this time',
};

export const interviewRecommendationLabel: Record<
  CandidateSummaryRecommendation,
  string
> = {
  STRONG_MATCH: 'Recommend interview',
  PARTIAL_MATCH: 'Recommend targeted interview',
  WEAK_MATCH: 'Do not recommend interview',
};

export const interviewRecommendationGuidance: Record<
  CandidateSummaryRecommendation,
  string
> = {
  STRONG_MATCH:
    'The resume provides enough evidence of the role’s must-have skills to proceed.',
  PARTIAL_MATCH:
    'Proceed only if the interview can validate the unclear or partially evidenced requirements.',
  WEAK_MATCH:
    'The current resume evidence does not support moving this application to interview.',
};

export const skillSourceLabel: Record<CandidateSummarySkillSource, string> = {
  RECRUITER_SELECTED: 'Must-have (recruiter shortlist)',
  JD_INFERRED: 'Must-have (from job description)',
};

export const skillStatusLabel: Record<CandidateSummarySkillStatus, string> = {
  MET: 'Evidence on resume',
  PARTIAL: 'Partial evidence',
  NOT_MET: 'Not demonstrated',
  UNCLEAR: 'Needs validation',
};

export const niceToHaveStatusLabel: Record<
  CandidateSummaryNiceToHaveStatus,
  string
> = {
  MET: 'Evidence on resume',
  PARTIAL: 'Partial evidence',
  NOT_MET: 'Not demonstrated',
  UNCLEAR: 'Needs validation',
};

export const recommendationTagColor: Record<
  CandidateSummaryRecommendation,
  string
> = {
  STRONG_MATCH: 'success',
  PARTIAL_MATCH: 'warning',
  WEAK_MATCH: 'error',
};

export const skillStatusColor: Record<CandidateSummarySkillStatus, string> = {
  MET: 'success',
  PARTIAL: 'warning',
  NOT_MET: 'error',
  UNCLEAR: 'default',
};

const requiredSkillStatusWeight: Record<CandidateSummarySkillStatus, number> = {
  MET: 1,
  PARTIAL: 0.65,
  UNCLEAR: 0.35,
  NOT_MET: 0,
};

export function calculateAiBriefFitScore(
  skills?: CandidateSummaryRequiredSkillAssessment[]
): number {
  if (!skills?.length) {
    return 0;
  }
  const weightedTotal = skills.reduce(
    (total, skill) => total + requiredSkillStatusWeight[skill.status],
    0
  );
  return Math.round((weightedTotal / skills.length) * 1000) / 10;
}

export const niceToHaveStatusColor: Record<
  CandidateSummaryNiceToHaveStatus,
  string
> = {
  MET: 'success',
  PARTIAL: 'warning',
  NOT_MET: 'error',
  UNCLEAR: 'default',
};

export const TABLE_COLUMNS = {
  mustHaveSkill: 'Must-have skill',
  skillSource: 'Skill source',
  resumeFit: 'Resume fit',
  resumeEvidence: 'Resume evidence',
  preferredSkill: 'Preferred skill',
} as const;

export interface CandidateAssessmentExportContext {
  candidateName?: string;
  jobTitle?: string;
  companyName?: string;
  generatedBy?: string;
  updatedAt?: string;
}

function formatTimestamp(value?: string): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatRequiredSkillLine(
  item: CandidateSummaryRequiredSkillAssessment
): string {
  return [
    item.skill,
    `  Resume fit: ${skillStatusLabel[item.status]}`,
    `  Skill source: ${skillSourceLabel[item.source]}`,
    `  Resume evidence: ${item.evidence}`,
  ].join('\n');
}

function formatPreferredSkillLine(
  item: CandidateSummaryNiceToHaveAssessment
): string {
  return [
    item.skill,
    `  Resume fit: ${niceToHaveStatusLabel[item.status]}`,
    `  Resume evidence: ${item.evidence}`,
  ].join('\n');
}

function appendBulletSection(
  lines: string[],
  title: string,
  items?: string[]
): void {
  lines.push(title);
  if (items?.length) {
    items.forEach((item) => lines.push(`• ${item}`));
  } else {
    lines.push('• None noted');
  }
  lines.push('');
}

/**
 * Plain-text export for clipboard — structured for sourcers and recruiters.
 */
export function formatCandidateAssessmentDocument(
  payload: CandidateSummaryPayload,
  context: CandidateAssessmentExportContext = {}
): string {
  const lines: string[] = [
    ASSESSMENT_SECTIONS.documentTitle.toUpperCase(),
    'Prepared for client review',
  ];

  if (context.companyName) {
    lines.push(`Client: ${context.companyName}`);
  }
  if (context.jobTitle) {
    lines.push(`Role: ${context.jobTitle}`);
  }
  if (context.candidateName) {
    lines.push(`Candidate: ${context.candidateName}`);
  }

  lines.push(
    '',
    `${ASSESSMENT_SECTIONS.overallRecommendation}: ${recommendationLabel[payload.recommendation]} — ${recommendationClientGuidance[payload.recommendation]}`,
    '',
    '─'.repeat(48),
    '',
    ASSESSMENT_SECTIONS.recruiterSummary.toUpperCase(),
    '',
    payload.headline,
    '',
    payload.executiveSummary,
    '',
    '─'.repeat(48),
    '',
    ASSESSMENT_SECTIONS.mustHaveSkills.toUpperCase(),
    ASSESSMENT_SECTIONS.mustHaveSkillsIntro,
    ''
  );

  if (payload.requiredSkillsAssessment?.length) {
    payload.requiredSkillsAssessment.forEach((item, index) => {
      if (index > 0) {
        lines.push('');
      }
      lines.push(formatRequiredSkillLine(item));
    });
  } else {
    lines.push('No must-have skills assessed.');
  }

  lines.push(
    '',
    '─'.repeat(48),
    '',
    ASSESSMENT_SECTIONS.preferredSkills.toUpperCase()
  );
  lines.push(ASSESSMENT_SECTIONS.preferredSkillsIntro, '');

  if (payload.niceToHaveAssessment?.length) {
    payload.niceToHaveAssessment.forEach((item, index) => {
      if (index > 0) {
        lines.push('');
      }
      lines.push(formatPreferredSkillLine(item));
    });
  } else {
    lines.push('No preferred skills assessed.');
  }

  lines.push('', '─'.repeat(48), '');
  appendBulletSection(
    lines,
    ASSESSMENT_SECTIONS.keyStrengths.toUpperCase(),
    payload.strengths
  );
  lines.push('─'.repeat(48), '');
  appendBulletSection(
    lines,
    ASSESSMENT_SECTIONS.gapsAndRisks.toUpperCase(),
    gapsForClientExport(payload)
  );
  lines.push('─'.repeat(48), '');
  lines.push(
    ASSESSMENT_SECTIONS.interviewRecommendation.toUpperCase(),
    `${interviewRecommendationLabel[payload.recommendation]} — ${interviewRecommendationGuidance[payload.recommendation]}`,
    '',
    '─'.repeat(48),
    ''
  );
  appendBulletSection(
    lines,
    ASSESSMENT_SECTIONS.interviewTalkingPoints.toUpperCase(),
    payload.interviewFocus
  );
  lines.push('─'.repeat(48), '', CANDIDATE_ASSESSMENT_DISCLAIMER);

  if (context.generatedBy) {
    lines.push(`Prepared by: ${context.generatedBy}`);
  }
  if (context.updatedAt) {
    lines.push(`Last updated: ${formatTimestamp(context.updatedAt)}`);
  } else if (payload.generatedAt) {
    lines.push(`Prepared: ${formatTimestamp(payload.generatedAt)}`);
  }

  return lines.join('\n').trim();
}

/** @deprecated Use formatCandidateAssessmentDocument */
export function formatCandidateSummaryText(
  payload: CandidateSummaryPayload
): string {
  return formatCandidateAssessmentDocument(payload);
}
